import type { YoginiDashaResponse, YoginiDashaPeriod } from "@/lib/api/yogini-dasha";
import type { ReactNode } from "react";
import AIAstrologerCTA from "@/components/AIAstrologerCTA";
import MethodologyTrustBlock from "@/components/MethodologyTrustBlock";
import Link from "next/link";

export function YoginiDashaResult({
  result,
  crossLinks,
}: {
  result: YoginiDashaResponse;
  crossLinks: ReactNode;
}) {
  const currentPeriod = result.periods.find((p) => p.is_current);

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12">
        <YoginiDashaHeader result={result} />
        <CurrentYoginiCard currentPeriod={currentPeriod} />
        <YoginiPredictions result={result} />
        <YoginiSequence result={result} />
        <YoginiDashaCTA />
        <div className="my-8">
          <MethodologyTrustBlock
            computes="Your 8 Yogini Dasha Cycle"
            note="Calculated from exact Moon nakshatra degrees using Swiss Ephemeris following Brihat Parashara Hora Shastra (Chapter 47)."
          />
        </div>
        <AIAstrologerCTA context="kundli" />
        {crossLinks}
      </div>
    </div>
  );
}

function YoginiDashaHeader({ result }: { result: YoginiDashaResponse }) {
  return (
    <div className="text-center mb-8">
      <p className="text-eyebrow font-semibold uppercase tracking-eyebrow mb-3 text-intent-brand">
        Vedic Yogini Dasha System
      </p>
      <h2 className="text-headline sm:text-display font-bold leading-tight font-serif text-ish-dark">
        Yogini Dasha for {result.name}
      </h2>
      <p className="text-body mt-2 text-ish-text-muted">
        Janma Nakshatra:{" "}
        <span className="font-semibold text-ish-dark">
          {result.moon_nakshatra} (Pada {result.moon_nakshatra_pada})
        </span>{" "}
        · Starting Yogini:{" "}
        <span className="font-semibold text-ish-dark">{result.starting_yogini_display}</span>
      </p>
    </div>
  );
}

function CurrentYoginiCard({ currentPeriod }: { currentPeriod: YoginiDashaPeriod | undefined }) {
  if (!currentPeriod) return null;
  const isAuspicious =
    currentPeriod.nature.includes("Shubha") || currentPeriod.nature.includes("Auspicious");

  return (
    <div
      className={`rounded-xl border p-5 sm:p-6 mb-6 text-center shadow-sm ${
        isAuspicious
          ? "bg-surface-warm border-ritual-gold/40"
          : "bg-surface-raised border-border-default"
      }`}
    >
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-ish-accent-light text-intent-brand mb-2">
        Active Yogini Mahadasha
      </span>
      <h3 className="text-2xl sm:text-3xl font-bold font-serif text-ish-dark mt-1">
        {currentPeriod.yogini_display} ({currentPeriod.yogini_hi})
      </h3>
      <p className="text-sm font-medium text-ish-text-muted mt-1">
        Governed by {currentPeriod.planet_display} · {currentPeriod.nature}
      </p>
      <div className="inline-block px-3 py-1 bg-white/80 rounded-md border border-ish-border text-xs font-mono font-semibold text-ish-dark mt-3">
        Active: {currentPeriod.start_date} → {currentPeriod.end_date} ({currentPeriod.years} yrs)
      </div>
      <p className="text-sm leading-relaxed text-ish-dark mt-3 max-w-md mx-auto">
        {currentPeriod.signification}
      </p>
    </div>
  );
}

function YoginiPredictions({ result }: { result: YoginiDashaResponse }) {
  if (result.predictions.length === 0) return null;
  return (
    <div className="card-sacred p-5 sm:p-6 mb-6">
      <h3 className="text-subtitle font-bold mb-4 font-serif text-ish-dark">
        Yogini Dasha Analysis
      </h3>
      <div className="space-y-4">
        {result.predictions.map((pred, i) => (
          <div key={i} className="rounded-lg bg-surface-raised p-4 border border-border-default">
            <p className="text-caption font-semibold uppercase tracking-wide mb-1 text-intent-brand">
              {pred.topic}
            </p>
            <p className="text-body leading-relaxed text-text-secondary">{pred.insight}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function YoginiSequence({ result }: { result: YoginiDashaResponse }) {
  return (
    <div className="card-sacred p-5 sm:p-6 mb-6">
      <h3 className="text-subtitle font-bold mb-2 font-serif text-ish-dark">
        Yogini Dasha Timeline (108-Year Cycle)
      </h3>
      <p className="text-body-small mb-5 text-ish-text-muted">
        The 8 Yoginis cycle every 36 years. Below is your full sequence across 3 complete cycles:
      </p>
      <div className="space-y-3">
        {result.periods.map((period, i) => (
          <div
            key={`${period.yogini}-${period.start_date}`}
            className={`rounded-lg border p-3 sm:p-4 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${
              period.is_current
                ? "border-ish-accent bg-ish-accent-light ring-1 ring-ish-accent"
                : "border-border-default bg-surface-raised"
            }`}
          >
            <div className="flex items-center gap-3">
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  period.is_current
                    ? "bg-surface-brand-strong text-white"
                    : "bg-surface-pressed text-text-secondary"
                }`}
              >
                {i + 1}
              </span>
              <div>
                <p className="text-sm font-semibold text-text-primary">
                  {period.yogini_display} ({period.yogini_hi})
                  {period.is_current && (
                    <span className="ml-2 inline-block rounded bg-surface-brand-strong px-2 py-0.5 text-eyebrow font-bold uppercase tracking-wider text-white">
                      Active Now
                    </span>
                  )}
                </p>
                <p className="text-xs text-text-muted">
                  Lord: {period.planet_display} · {period.nature}
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right text-xs text-text-secondary font-mono">
              <span>
                {period.start_date} – {period.end_date}
              </span>
              <span className="block text-text-muted text-eyebrow font-sans">
                {period.years} years
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function YoginiDashaCTA() {
  return (
    <div className="rounded-xl border border-ish-border bg-ish-accent-light p-6 mb-6 text-center">
      <h3 className="text-lg font-bold font-serif text-ish-dark mb-2">
        Need Complete Chart & Dasha Guidance?
      </h3>
      <p className="text-sm text-ish-text-muted mb-4 max-w-md mx-auto">
        Explore your complete birth chart with Vimshottari Mahadasha, Char Dasha, and Navamsa (D9).
      </p>
      <Link
        href="/free-kundali/"
        className="inline-block rounded-lg bg-surface-brand-strong px-6 py-2.5 text-sm font-semibold text-white hover:bg-surface-brand-strong/90 transition shadow-sm"
      >
        Generate Full Kundali Free
      </Link>
    </div>
  );
}
